/**
 * 灵感碎片、资格证门路、修行境界（纯数据）。
 *
 * 这一份是「隐性成长」的账本：
 *   碎片  — 某个标签攒到一定份上，心里冒出来的半句话（不告诉玩家阈值）
 *   门路  — 两块碎片撞上了，突然明白一件事，可能得一张资格证
 *   境界  — 灵气攒出来的修行进度，只影响吐纳与悟性，不显示数值
 *
 * 规矩还是那三条：不公示、不直给、不解释。
 * 玩家只看见「你忽然明白了什么」，看不见「restaurant >= 12」。
 */

/**
 * 灵感碎片。
 *  need  — 哪个标签攒到多少才冒出来（玩家不看见这几个数）
 *  sense — 冒出来时心里那句话，只给意象，不给规则
 */
export const FRAGMENTS = {
  flavor: { name: '火候的直觉', sense: '锅里那点声响，你听得出差在哪一刻。', need: { restaurant: 12 } },
  knife: { name: '刀下的分寸', sense: '切得快不快不在手上，在心里有数。', need: { restaurant: 30 } },
  soil: { name: '土性', sense: '抓一把土，捏得出它渴不渴。', need: { farming: 12 } },
  season: { name: '节气', sense: '什么时候下种，你不再看别人的脸色。', need: { farming: 28 } },
  route: { name: '路上的见闻', sense: '哪条路贵在快，哪条贵在稳，走几趟就知道了。', need: { hauling: 18 } },
  price: { name: '货物的来路', sense: '一样的东西，搁在不同地方就是两个价。', need: { trading: 14 } },
  temper: { name: '机器的脾气', sense: '它什么时候要闹，你提前半息就听见了。', need: { machinery: 14 } },
  handy: { name: '手上的准头', sense: '手比眼睛先到地方。', need: { skillful: 14 } },
  strength: { name: '身上的力气', sense: '哪一寸力气该用在什么时候，心里有杆秤。', need: { labor: 20 } },
  kind: { name: '与人方便', sense: '帮过的人，路上会跟你打招呼。', need: { aid: 14 } },
  trust: { name: '街坊的托付', sense: '有人把钥匙交给你，让你替他看一天门。', need: { trustOfLocals: 14 } },
  quiet: { name: '静', sense: '坐下来的时候，屋里的声音一层层退下去。', need: { insight: 16 } },
  book: { name: '看过的字', sense: '看过的字，会在你不留意的时候冒出来。', need: { learned: 14 } },
  face: { name: '脸面', sense: '你在街上走过，有人让半步。', need: { face: 20 } },
};

/**
 * 门路：两块碎片相遇，突然明白一件事，得一张资格证。
 * 每门分四级，一级一级往上；级级都要两块碎片（后一级更难凑）。
 * rate 是「撞上了也不一定想通」的基础概率。
 *
 * need 可以写成一组碎片（['flavor','soil']），
 * 也可以写成几组任选（[['flavor','soil'],['flavor','route']]）——
 * 同一件事，从地里想通、从路上想通、从市面想通，都算数。
 * 需求里的例子就是这么来的：火候的直觉 + 路上的见闻 -> 食材的来路 -> 厨子。
 *
 * perk 是「这张照能换来什么」——有进必有出，照不是摆着看的：
 *   cookFail  ×相乘（失手打几折）   cookEffect 取最大（下肚的效验翻几倍）
 *   unlock    能做的菜与丹（没那张照，讲究的做不出来）
 *   yield     一畦多收几把           seedRate    收获留种的机会
 *   channelOff御气少花几点           sellBonus   卖价加成   buyOff 买价折扣
 *   craftOff  攒车少失手             qiBonus     读书吐纳多长几点
 *   insightBonus 悟道的概率加成
 */
export const TRACKS = {
  cook: {
    name: '灶上',
    levels: [
      {
        cert: 'cook_1',
        name: '火房帮工',
        need: [['flavor', 'soil'], ['flavor', 'route'], ['flavor', 'price']],
        rate: 0.34,
        say: '盐放多少，手自己知道。',
        perk: { cookFail: 0.8, unlock: ['herbMeal'] },
      },
      {
        cert: 'cook_2',
        name: '灶上伙计',
        need: ['flavor', 'knife'],
        rate: 0.28,
        say: '火要先旺后稳，急不得。',
        perk: { cookFail: 0.8, cookEffect: 1.1, unlock: ['clearHeart', 'restore'] },
      },
      {
        cert: 'cook_3',
        name: '掌勺的',
        need: ['knife', 'price'],
        rate: 0.22,
        say: '什么料配什么价，看一眼就准。',
        perk: { cookFail: 0.8, cookEffect: 1.2, unlock: ['feast'] },
      },
      {
        cert: 'cook_4',
        name: '灶头师傅',
        need: ['knife', 'season'],
        rate: 0.16,
        say: '一桌菜有个先后，乱了就没味。',
        // 那句老话：待客的菜，做的时候多留一份
        perk: { cookFail: 0.7, cookEffect: 1.3, leftOver: 1 },
      },
    ],
  },
  farm: {
    name: '地里',
    levels: [
      {
        cert: 'farm_1',
        name: '会伺弄',
        need: ['soil', 'kind'],
        rate: 0.34,
        say: '苗跟人一样，得天天照面。',
        perk: { seedRate: 0.25 },
      },
      {
        cert: 'farm_2',
        name: '地里熟手',
        need: ['soil', 'strength'],
        rate: 0.28,
        say: '一锄头下去多深，手上有准。',
        perk: { yield: 1 },
      },
      {
        cert: 'farm_3',
        name: '老把式',
        need: ['season', 'soil'],
        rate: 0.22,
        say: '看云就知道这两天该不该浇。',
        perk: { channelOff: 5 },
      },
      {
        cert: 'farm_4',
        name: '一方的农把式',
        need: ['season', 'trust'],
        rate: 0.16,
        say: '左邻右舍都来问你什么时候下种。',
        perk: { yield: 1, seedRate: 0.25 },
      },
    ],
  },
  trade: {
    name: '买卖',
    levels: [
      {
        cert: 'trade_1',
        name: '跑腿的',
        need: ['route', 'price'],
        rate: 0.34,
        say: '货不等人，人得等货。',
        perk: { sellBonus: 0.03, buyOff: 0.02 },
      },
      {
        cert: 'trade_2',
        name: '贩子',
        need: ['price', 'face'],
        rate: 0.28,
        say: '价钱是谈出来的，不是喊出来的。',
        perk: { sellBonus: 0.03, buyOff: 0.02 },
      },
      {
        cert: 'trade_3',
        name: '行商',
        need: ['route', 'trust'],
        rate: 0.22,
        say: '路上有人肯带你一程，那也是本钱。',
        perk: { sellBonus: 0.03, buyOff: 0.02 },
      },
      {
        cert: 'trade_4',
        name: '老客商',
        need: ['price', 'book'],
        rate: 0.16,
        say: '哪一处缺什么，你早在半个月前就听说了。',
        perk: { sellBonus: 0.03, buyOff: 0.02 },
      },
    ],
  },
  craft: {
    name: '作场',
    levels: [
      {
        cert: 'craft_1',
        name: '上手',
        need: ['handy', 'temper'],
        rate: 0.34,
        say: '东西拆得开，就装得回去。',
        perk: { craftOff: 0.03 },
      },
      {
        cert: 'craft_2',
        name: '帮工',
        need: ['handy', 'strength'],
        rate: 0.28,
        say: '拧到什么程度算紧，不必再看扳手。',
        perk: { craftOff: 0.03 },
      },
      {
        cert: 'craft_3',
        name: '作头',
        need: ['temper', 'book'],
        rate: 0.22,
        say: '它响得不对，你就知道是哪一颗螺丝。',
        perk: { craftOff: 0.03 },
      },
      {
        cert: 'craft_4',
        name: '老师傅',
        need: ['handy', 'season'],
        rate: 0.16,
        say: '什么样的料配什么样的工，心里有谱。',
        perk: { craftOff: 0.03 },
      },
    ],
  },
  scholar: {
    name: '读书',
    levels: [
      {
        cert: 'scholar_1',
        name: '识字的',
        need: ['book', 'quiet'],
        rate: 0.34,
        say: '字认得多了，话就想得慢一点。',
        perk: { qiBonus: 1, insightBonus: 0.02 },
      },
      {
        cert: 'scholar_2',
        name: '能写会算',
        need: ['book', 'face'],
        rate: 0.28,
        say: '账目看得明白，人也跟着清爽。',
        perk: { qiBonus: 1, insightBonus: 0.02 },
      },
      {
        cert: 'scholar_3',
        name: '明理的',
        need: ['book', 'kind'],
        rate: 0.22,
        say: '理不是用来压人的，是用来安人的。',
        perk: { qiBonus: 1, insightBonus: 0.02 },
      },
      {
        cert: 'scholar_4',
        name: '先生',
        need: ['book', 'trust'],
        rate: 0.16,
        say: '有人把孩子送来，让你教他认字。',
        perk: { qiBonus: 1, insightBonus: 0.02 },
      },
    ],
  },
};

/**
 * 修行境界：灵气攒出来的进度（累计得过多少灵气，不是身上还剩多少）。
 * 不显示数值，只在那一天给一句含糊的话。
 */
export const REALMS = [
  { level: 1, name: '感气', need: 60, say: '坐得久了，身上有股细细的暖意，顺着脊背慢慢爬。', perk: '吐纳更沉' },
  { level: 2, name: '通脉', need: 200, say: '这一坐，气自己找着路走，不再要你领着。', perk: '心里更静' },
  { level: 3, name: '凝神', need: 500, say: '窗外过车，你听得见，却不妨碍你。', perk: '念头更易聚拢' },
  { level: 4, name: '忘我', need: 1000, say: '收功那一刻，忘了自己坐了多久。', perk: '灵气用得更省' },
];

/** 御气催熟：一口气把一畦苗往前推一段 */
export const CHANNEL_COST = 25;

/** 把某一级的 need 统一成「几组任选」的形式：[[a,b],[a,c]] */
export function needCombos(level) {
  const need = (level && level.need) || [];
  return Array.isArray(need[0]) ? need : [need];
}

export function realmOf(qiEver) {
  let cur = null;
  for (const r of REALMS) if (qiEver >= r.need) cur = r;
  return cur;
}

export function realmName(level) {
  const r = REALMS.find((x) => x.level === level);
  return r ? r.name : '未入门';
}
